import { Col } from 'components';
import { useInfiniteScrollObserver } from 'core';
import { Api } from 'lib';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ParticipationSummary } from 'screens/Admin/components';
import Filters from './Filters';
import List from './List';
import ShoutoutPanel from './ShoutoutPanel';

const fmtNumber = (n) => (n || 0).toLocaleString();

// Names the exact slice the counts describe: sport first, then who they belong to. Team and
// athlete are mutually exclusive, so at most one party ever appears.
const summaryNote = (sport, team, athlete) => {
  const subject = sport.opt ? `Total ${sport.value} shoutouts` : 'Total shoutouts';

  if (team) return `${subject} for ${team.name}`;
  if (athlete) return `${subject} for ${athlete.name}`;

  return `${subject}`;
};

const AllShoutouts = () => {
  const [athlete, setAthlete] = useState(null),
    [team, setTeam] = useState(null),
    [sport, setSport] = useState({ value: 'All' }),
    [dataId, setDataId] = useState(null),
    [visibleShoutouts, setVisibleShoutouts] = useState([]),
    [loading, setLoading] = useState(true),
    [summary, setSummary] = useState(null),
    // Starts true so the first paint is the loader, not a momentary row of zeros.
    [summaryLoading, setSummaryLoading] = useState(true),
    shoutouts = useSelector((state) => state.admin?.shoutouts),
    fetching = useRef(false),
    bottom = useRef(false),
    skip = useRef(0),
    dispatch = useDispatch(),
    scrollId = 'admin-shoutouts-list',
    observerTarget = useRef(null);

  useInfiniteScrollObserver(observerTarget, getShoutouts, bottomTrigger);

  useEffect(() => {
    getShoutouts(true).then();
    getSummary().then();
  }, []);

  useEffect(() => {
    if (fetching.current || loading) {
      return;
    }

    getShoutouts(true).then();
  }, [athlete, team, sport]);

  // Every filter narrows the counts here — unlike the fancam header, none of the filters
  // is the dimension the counts are split by.
  useEffect(() => {
    if (loading) return;
    getSummary().then();
  }, [athlete, team, sport]);

  useEffect(() => {
    setVisibleShoutouts(shoutouts || []);
  }, [shoutouts]);

  useEffect(() => {
    if (dataId && !visibleShoutouts?.find((x) => x._id === dataId)) {
      setDataId(null);
    }
  }, [dataId, visibleShoutouts]);

  function bottomTrigger(value) {
    bottom.current = value;
  }

  function handleDelete(shoutoutId) {
    setVisibleShoutouts((prev) => prev.filter((x) => x._id !== shoutoutId));
    if (dataId === shoutoutId) {
      setDataId(null);
    }
    getSummary().then();
  }

  async function getShoutouts(isFilterChange) {
    if (isFilterChange) {
      dispatch({
        type: 'ADMIN_SET_SHOUTOUTS',
        payload: []
      });
      setLoading(true);
      skip.current = 0;
      bottomTrigger(false);
    }

    if (fetching.current || bottom.current) {
      return;
    }

    fetching.current = true;

    /**
     * @property {number} length
     */
    const res = await Api.post('/admin/getShoutouts', {
      skip: skip.current,
      sport: sport.opt,
      team: team?._id,
      athlete: athlete?._id
    });

    fetching.current = false;
    setLoading(false);

    if (!res || res.length === 0) {
      bottomTrigger(true);
      return;
    }

    bottomTrigger(false);
    skip.current += res.length;

    dispatch({
      type: 'ADMIN_ADD_SHOUTOUTS',
      payload: res
    });
  }

  // Aggregate counts for the current filter set. Independent of the list's paging — these
  // describe the whole filtered set, not just the rows loaded so far.
  async function getSummary() {
    setSummaryLoading(true);

    const params = new URLSearchParams();
    if (sport.opt) params.set('sport', sport.opt);
    if (team) params.set('team', team._id);
    if (athlete) params.set('athlete', athlete._id);
    const qs = params.toString();

    const res = await Api.get(`/admin/shoutouts/summary${qs ? `?${qs}` : ''}`, null, {
      silent: true
    });

    setSummary(res?.data || null);
    setSummaryLoading(false);
  }

  // A party filter already pins the rows to one kind of shoutout, so the split would just
  // restate the total — show the total alone. The sport filter is orthogonal: it scopes the
  // total, and the split still breaks that total down.
  const partyFiltered = Boolean(team || athlete);

  return (
    <>
      {/* min-height fills the tab area so the sport-filter dropdown isn't
          clipped by the scroll container when the list is empty
          (100vh - 54px header - 44px tab bar) */}
      <Col start noFlex wid="100%" minHt="calc(100vh - 98px)" id={scrollId}>
        <Filters
          sport={sport}
          setSport={setSport}
          team={team}
          setTeam={setTeam}
          athlete={athlete}
          setAthlete={setAthlete}
          className="filters"
        />

        <Col noFlex wid="100%" pad="0 24px">
          <ParticipationSummary
            loading={summaryLoading}
            // note={summaryNote(sport, team, athlete)}
            items={[
              { label: 'Total', value: fmtNumber(summary?.total) },
              { label: 'For athletes', value: fmtNumber(summary?.athlete), type: 'athlete' },
              { label: 'For teams', value: fmtNumber(summary?.team), type: 'team' }
            ].filter((item) => !item.type || !partyFiltered)}
          />
        </Col>

        <List
          data={visibleShoutouts}
          loading={loading}
          dataId={dataId}
          onClick={(id) => setDataId(id)}
          onDelete={handleDelete}
        />
        <div ref={observerTarget}></div>
      </Col>

      <ShoutoutPanel dataId={dataId} closeModal={() => setDataId(null)} />
    </>
  );
};

export default AllShoutouts;
